import React, { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Popover,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  FormGroup,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/* ── i18n labels ── */
const L = {
  es: {
    noFee: 'Sin cuota',
    bank: 'Banco',
    reward: 'Recompensa',
    rewardType: 'Tipo de recompensa',
    resetFilters: 'Restablecer filtros',
    cashback: 'Cash back', points: 'Puntos', miles: 'Millas', noReward: 'Sin recompensa',
  },
  en: {
    noFee: 'No fee',
    bank: 'Bank',
    reward: 'Reward',
    rewardType: 'Reward type',
    resetFilters: 'Reset filters',
    cashback: 'Cash back', points: 'Points', miles: 'Miles', noReward: 'No rewards',
  },
  fr: {
    noFee: 'Sans frais',
    bank: 'Banque',
    reward: 'Récompense',
    rewardType: 'Type de récompense',
    resetFilters: 'Réinitialiser les filtres',
    cashback: 'Remises', points: 'Points', miles: 'Milles', noReward: 'Sans récompense',
  },
};

const QuickFilterBar = ({ selectedFilters, onFilterChange, onClearFilters, allCards = [] }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const l = L[lang] || L.es;

  const REWARD_OPTIONS = [
    { value: 'cashback', label: l.cashback },
    { value: 'points', label: l.points },
    { value: 'miles', label: l.miles },
    { value: 'none', label: l.noReward },
  ];
  const [bankAnchor, setBankAnchor] = useState(null);
  const [rewardAnchor, setRewardAnchor] = useState(null);

  const safe = selectedFilters || {
    newcomer_friendly: false,
    no_annual_fee: false,
    bank: [],
    purchase_rate: [0, 30],
    reward_type: [],
  };

  const bankOptions = useMemo(() => {
    const counts = {};
    allCards.forEach((c) => {
      if (c.bank) counts[c.bank] = (counts[c.bank] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([bank, count]) => ({ value: bank, label: `${bank} (${count})` }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [allCards]);

  const hasActiveFilters =
    safe.newcomer_friendly ||
    safe.no_annual_fee ||
    safe.bank.length > 0 ||
    safe.reward_type.length > 0 ||
    (safe.purchase_rate && (safe.purchase_rate[0] > 0 || safe.purchase_rate[1] < 30));

  const handleBankToggle = (bank) => {
    const current = safe.bank || [];
    const next = current.includes(bank) ? current.filter((b) => b !== bank) : [...current, bank];
    onFilterChange('bank', next);
  };

  const handleRewardToggle = (type) => {
    const current = safe.reward_type || [];
    const next = current.includes(type) ? current.filter((t) => t !== type) : [...current, type];
    onFilterChange('reward_type', next);
  };

  return (
    <Box
      sx={{
        mb: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: '#FFFFFF',
        transition: 'box-shadow 0.2s ease',
      }}
    >
      {/* Filter chips row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflowX: 'auto',
          pb: 1.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 2 },
        }}
      >
        <FilterListIcon sx={{ color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }} />

        {/* Newcomers toggle */}
        <Chip
          label="Newcomers"
          variant={safe.newcomer_friendly ? 'filled' : 'outlined'}
          onClick={() => onFilterChange('newcomer_friendly', !safe.newcomer_friendly)}
          sx={{
            fontWeight: 600,
            bgcolor: safe.newcomer_friendly ? 'primary.main' : 'transparent',
            color: safe.newcomer_friendly ? '#fff' : 'text.primary',
            borderColor: 'divider',
            boxShadow: safe.newcomer_friendly ? '0 0 0 2px rgba(30,58,95,0.15)' : 'none',
            '&:hover': {
              bgcolor: safe.newcomer_friendly ? 'primary.dark' : 'action.hover',
            },
          }}
        />

        {/* No fee toggle */}
        <Chip
          label={l.noFee}
          variant={safe.no_annual_fee ? 'filled' : 'outlined'}
          onClick={() => onFilterChange('no_annual_fee', !safe.no_annual_fee)}
          sx={{
            fontWeight: 600,
            bgcolor: safe.no_annual_fee ? 'primary.main' : 'transparent',
            color: safe.no_annual_fee ? '#fff' : 'text.primary',
            borderColor: 'divider',
            boxShadow: safe.no_annual_fee ? '0 0 0 2px rgba(30,58,95,0.15)' : 'none',
            '&:hover': {
              bgcolor: safe.no_annual_fee ? 'primary.dark' : 'action.hover',
            },
          }}
        />

        {/* Bank dropdown */}
        <Chip
          label={safe.bank.length > 0 ? `${l.bank} (${safe.bank.length})` : l.bank}
          variant={safe.bank.length > 0 ? 'filled' : 'outlined'}
          deleteIcon={<ExpandMoreIcon />}
          onDelete={(e) => setBankAnchor(e.currentTarget.parentElement)}
          onClick={(e) => setBankAnchor(e.currentTarget)}
          sx={{
            fontWeight: 600,
            bgcolor: safe.bank.length > 0 ? 'primary.main' : 'transparent',
            color: safe.bank.length > 0 ? '#fff' : 'text.primary',
            borderColor: 'divider',
            boxShadow: safe.bank.length > 0 ? '0 0 0 2px rgba(30,58,95,0.15)' : 'none',
            '& .MuiChip-deleteIcon': {
              color: safe.bank.length > 0 ? '#fff' : 'text.secondary',
            },
          }}
        />
        <Popover
          open={Boolean(bankAnchor)}
          anchorEl={bankAnchor}
          onClose={() => setBankAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, maxHeight: 300, minWidth: 220 } } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{l.bank}</Typography>
          <FormGroup sx={{ maxHeight: 220, overflowY: 'auto' }}>
            {bankOptions.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={safe.bank.includes(opt.value)}
                    onChange={() => handleBankToggle(opt.value)}
                    size="small"
                    sx={{ py: 0.3 }}
                  />
                }
                label={<Typography variant="body2">{opt.label}</Typography>}
              />
            ))}
          </FormGroup>
        </Popover>

        {/* Reward type dropdown */}
        <Chip
          label={safe.reward_type.length > 0 ? `${l.reward} (${safe.reward_type.length})` : l.reward}
          variant={safe.reward_type.length > 0 ? 'filled' : 'outlined'}
          deleteIcon={<ExpandMoreIcon />}
          onDelete={(e) => setRewardAnchor(e.currentTarget.parentElement)}
          onClick={(e) => setRewardAnchor(e.currentTarget)}
          sx={{
            fontWeight: 600,
            bgcolor: safe.reward_type.length > 0 ? 'primary.main' : 'transparent',
            color: safe.reward_type.length > 0 ? '#fff' : 'text.primary',
            borderColor: 'divider',
            boxShadow: safe.reward_type.length > 0 ? '0 0 0 2px rgba(30,58,95,0.15)' : 'none',
            '& .MuiChip-deleteIcon': {
              color: safe.reward_type.length > 0 ? '#fff' : 'text.secondary',
            },
          }}
        />
        <Popover
          open={Boolean(rewardAnchor)}
          anchorEl={rewardAnchor}
          onClose={() => setRewardAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, minWidth: 180 } } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{l.rewardType}</Typography>
          <FormGroup>
            {REWARD_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={safe.reward_type.includes(opt.value)}
                    onChange={() => handleRewardToggle(opt.value)}
                    size="small"
                    sx={{ py: 0.3 }}
                  />
                }
                label={<Typography variant="body2">{opt.label}</Typography>}
              />
            ))}
          </FormGroup>
        </Popover>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button
            variant="outlined"
            size="small"
            onClick={onClearFilters}
            startIcon={<CloseIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              textTransform: 'none',
              color: '#D32F2F',
              borderColor: 'rgba(211,47,47,0.3)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              fontWeight: 600,
              borderRadius: '999px',
              '&:hover': { borderColor: '#D32F2F', bgcolor: 'rgba(211,47,47,0.04)' },
            }}
          >
            {l.resetFilters}
          </Button>
        )}
      </Box>

      {/* Active filter tags */}
      {hasActiveFilters && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {safe.newcomer_friendly && (
            <Chip
              label="Newcomers"
              size="small"
              onDelete={() => onFilterChange('newcomer_friendly', false)}
              sx={{ fontWeight: 500 }}
            />
          )}
          {safe.no_annual_fee && (
            <Chip
              label={l.noFee}
              size="small"
              onDelete={() => onFilterChange('no_annual_fee', false)}
              sx={{ fontWeight: 500 }}
            />
          )}
          {safe.bank.map((b) => (
            <Chip
              key={b}
              label={b}
              size="small"
              onDelete={() => handleBankToggle(b)}
              sx={{ fontWeight: 500 }}
            />
          ))}
          {safe.reward_type.map((r) => (
            <Chip
              key={r}
              label={REWARD_OPTIONS.find((o) => o.value === r)?.label || r}
              size="small"
              onDelete={() => handleRewardToggle(r)}
              sx={{ fontWeight: 500 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(QuickFilterBar);
